import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RatingModal = ({
    isOpen,
    onClose,
    onRate,
    onSkip,
    title,
    subtitle,
    commentPlaceholder,
    submitLabel,
    skipLabel,
    initialRating = 0
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [rating, setRating] = useState(initialRating);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (rating === 0) {
            setError(t('ratings.errors.required', { defaultValue: 'Please select a rating' }));
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await onRate({ rating, comment });
            onClose();
        } catch (err) {
            console.error('Error submitting rating:', err);
            const message = err.response?.data?.message || t('ratings.errors.submitFailed', { defaultValue: 'Failed to submit rating' });
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleSkip = () => {
        if (onSkip) {
            onSkip();
        }
        onClose();
    };

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div className="modal-content" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                width: '90%',
                maxWidth: '400px',
                position: 'relative',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        right: isRTL ? 'auto' : '16px',
                        left: isRTL ? '16px' : 'auto',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6B7280'
                    }}
                >
                    <X size={24} />
                </button>

                <h2 style={{
                    marginTop: 0,
                    marginBottom: '8px',
                    fontSize: '20px',
                    fontWeight: '600',
                    textAlign: 'center',
                    color: '#111827'
                }}>
                    {title || t('ratings.title', { defaultValue: 'Rate Experience' })}
                </h2>

                <p style={{
                    textAlign: 'center',
                    color: '#6B7280',
                    marginBottom: '24px',
                    fontSize: '14px'
                }}>
                    {subtitle || t('ratings.subtitle', { defaultValue: 'How was your experience?' })}
                </p>

                <form onSubmit={handleSubmit}>
                    <div
                        onMouseLeave={() => setHoverRating(0)}
                        style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}
                    >
                        {[1, 2, 3, 4, 5].map((starValue) => {
                            const displayValue = hoverRating || rating;
                            const isFull = displayValue >= starValue;
                            const isHalf = displayValue >= starValue - 0.5 && !isFull;

                            return (
                                <div
                                    key={starValue}
                                    style={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        width: '32px',
                                        height: '32px',
                                        transition: 'transform 0.1s'
                                    }}
                                >
                                    {/* Left half hit area */}
                                    <div
                                        style={{ position: 'absolute', left: 0, width: '50%', height: '100%', zIndex: 10 }}
                                        onMouseEnter={() => setHoverRating(starValue - 0.5)}
                                        onClick={() => setRating(starValue - 0.5)}
                                    />
                                    {/* Right half hit area */}
                                    <div
                                        style={{ position: 'absolute', right: 0, width: '50%', height: '100%', zIndex: 10 }}
                                        onMouseEnter={() => setHoverRating(starValue)}
                                        onClick={() => setRating(starValue)}
                                    />

                                    {/* The Star visual layer */}
                                    <div style={{ position: 'relative', pointerEvents: 'none' }}>
                                        <Star
                                            size={32}
                                            color="#D1D5DB"
                                            strokeWidth={1.5}
                                        />
                                        {(isFull || isHalf) && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: isFull ? '100%' : '50%',
                                                height: '100%',
                                                overflow: 'hidden'
                                            }}>
                                                <Star
                                                    size={32}
                                                    fill="#FBBF24"
                                                    color="#FBBF24"
                                                    strokeWidth={1.5}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={commentPlaceholder || t('ratings.commentPlaceholder', { defaultValue: 'Share your experience (optional)...' })}
                            style={{
                                width: '100%',
                                minHeight: '100px',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid #D1D5DB',
                                resize: 'none',
                                fontFamily: 'inherit',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    {error && (
                        <div style={{
                            color: '#EF4444',
                            fontSize: '14px',
                            textAlign: 'center',
                            marginBottom: '16px',
                            backgroundColor: '#FEE2E2',
                            padding: '8px',
                            borderRadius: '6px'
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid #D1D5DB',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('common.cancel', { defaultValue: 'Cancel' })}
                            </button>
                            <button
                                type="submit"
                                disabled={loading || rating === 0}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: loading || rating === 0 ? '#9CA3AF' : '#10B981',
                                    color: 'white',
                                    fontWeight: '500',
                                    cursor: loading || rating === 0 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {loading ? t('common.submitting', { defaultValue: 'Submitting...' }) : (submitLabel || t('common.submit', { defaultValue: 'Submit' }))}
                            </button>
                        </div>

                        {onSkip && (
                            <button
                                type="button"
                                onClick={handleSkip}
                                style={{
                                    width: '100%',
                                    background: 'none',
                                    border: 'none',
                                    color: '#6B7280',
                                    fontSize: '14px',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    marginTop: '4px'
                                }}
                            >
                                {skipLabel || t('ratings.skip', { defaultValue: 'Skip for now' })}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RatingModal;
